import { RouteRecordRaw } from 'vue-router';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { requireAuth, setDayjsTimezone } from '@/common/router/navigationGuard';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import { VIEW_MODE } from '@/common/define/common.define';
import { requireDomainTargetOrTutorial } from '@/common/router/authGuard';

const PREFIX_CLOUD = 'cloud_';
const SUB_MENU_PREFIX = {
  aws: `${PREFIX_CLOUD}AWS_`,
};

const SUB_MENU_COMP_NAME = {
  ec2: `${SUB_MENU_PREFIX.aws}EC2`,
  lambda: `${SUB_MENU_PREFIX.aws}Lambda`,
  s3: `${SUB_MENU_PREFIX.aws}S3`,
  dynamoDb: `${SUB_MENU_PREFIX.aws}DynamoDB`,
  documentDb: `${SUB_MENU_PREFIX.aws}DocumentDB`,
  elastiCache: `${SUB_MENU_PREFIX.aws}ElastiCache`,
  rds: `${SUB_MENU_PREFIX.aws}RDS`,
  redshift: `${SUB_MENU_PREFIX.aws}Redshift`,
  dms: `${SUB_MENU_PREFIX.aws}DMS`,
  costExplorer: `${SUB_MENU_PREFIX.aws}CostExplorer`,
};

const cloudMenu: RouteRecordRaw[] = [
  {
    path: '/cloud/',
    name: `${PREFIX_CLOUD}Cloud`,
    component: BaseViewport,
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('cloud'), setDayjsTimezone],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD,
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
    children: [
      {
        path: 'computing',
        name: `${PREFIX_CLOUD}Computing`,
        component: () => import('@/cloud/views/cloudService/CloudServiceView.vue'),
        beforeEnter: [requireAuth, setDayjsTimezone],
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_COMPUTING,
          remountOnPathChange: true,
        },
      },
      {
        path: 'storage',
        name: `${PREFIX_CLOUD}Storage`,
        component: () => import('@/cloud/views/cloudService/CloudServiceView.vue'),
        beforeEnter: [requireAuth, setDayjsTimezone],
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_STORAGE,
          remountOnPathChange: true,
        },
      },
      {
        path: 'database',
        name: `${PREFIX_CLOUD}Database`,
        component: () => import('@/cloud/views/cloudService/CloudServiceView.vue'),
        beforeEnter: [requireAuth, setDayjsTimezone],
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_DATABASE,
          remountOnPathChange: true,
        },
      },
    ],
  },
  {
    path: '/cloud/aws/',
    name: SUB_MENU_PREFIX.aws,
    component: BaseViewport,
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('cloud'), setDayjsTimezone],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS,
      viewport: VIEWPORT_TYPE.REALTIME,
    },
    children: [
      {
        path: 'ec2',
        name: SUB_MENU_COMP_NAME.ec2,
        component: () => import('@/cloud/views/aws/ec2View/Ec2View.vue'),
        meta: {
          category: 'computing',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_EC2,
          prefixIcon: 'icon-aws-ec2',
        },
      },
      {
        path: 'lambda',
        name: SUB_MENU_COMP_NAME.lambda,
        component: () => import('@/cloud/views/aws/lambdaView/LambdaView.vue'),
        meta: {
          category: 'computing',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_LAMBDA,
          prefixIcon: 'icon-aws-lambda',
        },
      },
      {
        path: 's3',
        name: SUB_MENU_COMP_NAME.s3,
        component: () => import('@/cloud/views/aws/s3View/S3View.vue'),
        meta: {
          category: 'storage',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_S3,
          prefixIcon: 'icon-aws-s3',
        },
      },
      {
        path: 'document-db',
        name: SUB_MENU_COMP_NAME.documentDb,
        component: () => import('@/cloud/views/aws/documentDbView/DocumentDbView.vue'),
        meta: {
          category: 'database',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_DOCUMENT_DB,
          prefixIcon: 'icon-aws-documentdb',
        },
      },
      {
        path: 'dynamo-db',
        name: SUB_MENU_COMP_NAME.dynamoDb,
        component: () => import('@/cloud/views/aws/dynamoDbView/DynamoDbView.vue'),
        meta: {
          category: 'database',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_DYNAMO_DB,
          prefixIcon: 'icon-aws-dynamodb',
        },
      },
      {
        path: 'elasti-cache',
        name: SUB_MENU_COMP_NAME.elastiCache,
        component: () => import('@/cloud/views/aws/elastiCacheView/ElastiCacheView.vue'),
        meta: {
          category: 'database',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_ELASTI_CACHE,
          prefixIcon: 'icon-aws-elasticache',
        },
      },
      {
        path: 'redshift',
        name: SUB_MENU_COMP_NAME.redshift,
        component: () => import('@/cloud/views/aws/redshiftView/RedshiftView.vue'),
        meta: {
          category: 'database',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_REDSHIFT,
          prefixIcon: 'icon-aws-redshift',
        },
      },
      {
        path: 'dms',
        name: SUB_MENU_COMP_NAME.dms,
        component: () => import('@/cloud/views/aws/dms/DatabaseMigrationServiceView.vue'),
        meta: {
          category: 'database',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_DATABASE_MIGRATION,
          prefixIcon: 'icon-aws-dms',
        },
      },
      {
        path: 'cost-explorer',
        name: SUB_MENU_COMP_NAME.costExplorer,
        component: () => import('@/cloud/views/aws/costExplorerView/CostExplorerView.vue'),
        meta: {
          category: 'management',
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_COST_EXPLORER,
          prefixIcon: 'icon-aws-costexplorer',
        },
      },
    ],
  },
];

const getCloudNaviMenu = (
  subMenu?: 'aws',
  category?: 'computing' | 'storage' | 'database' | 'management',
) => {
  const filteredMenu = extractAccessibleRoutes(cloudMenu);

  if (subMenu && SUB_MENU_PREFIX[subMenu] && category) {
    return filteredMenu
      .filter((menu) => menu.name === SUB_MENU_PREFIX[subMenu])
      .map((menu) => {
        return {
          ...menu,
          children:
            menu.children?.filter((child) => {
              return (child?.meta?.category ?? '') === category;
            }) ?? [],
        };
      });
  }

  return filteredMenu;
};

export { cloudMenu, getCloudNaviMenu };
